import React, { useMemo, useState } from 'react';
import Table from 'react-bootstrap/Table';
import { ALL } from '../../shared/PlayerListFilters';

const projectionValue = (player) => {
  if (player.auction_value == null) return null;
  const value = Number(player.auction_value);
  return Number.isFinite(value) ? value : null;
};

const ContractSummary = ({ teams, players, selectedTeam, onTeamSelect }) => {
  const [sortKey, setSortKey] = useState('surplus');
  const [sortDir, setSortDir] = useState('desc');

  const rows = useMemo(() => {
    const summaries = teams.map((team) => ({
      team_key: team.team_key,
      team_name: team.team_name,
      team_logo_url: team.team_logo_url,
      contracts: 0,
      years: 0,
      salary: 0,
      marketValue: 0,
      surplus: 0,
    }));
    const summaryByTeam = Object.fromEntries(
      summaries.map((summary) => [summary.team_key, summary])
    );

    players.forEach((player) => {
      const summary = summaryByTeam[player.team_key];
      if (!summary) return;

      const salary = Number(player.salary) || 0;
      const marketValue = projectionValue(player);
      summary.contracts += 1;
      summary.years += Number(player.contract_length) || 0;
      summary.salary += salary;

      if (marketValue != null) {
        summary.marketValue += marketValue;
        summary.surplus += marketValue - salary;
      }
    });

    const direction = sortDir === 'asc' ? 1 : -1;
    return summaries.sort((a, b) => {
      if (sortKey === 'team') {
        return a.team_name.localeCompare(b.team_name) * direction;
      }
      return (Math.round(a[sortKey]) - Math.round(b[sortKey])) * direction;
    });
  }, [teams, players, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'team' ? 'asc' : 'desc');
  };

  const sortIndicator = (key) => {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const heading = (key, label) => (
    <button type="button" className="sortable-th" onClick={() => toggleSort(key)}>
      {label}{sortIndicator(key)}
    </button>
  );

  return (
    <div className="contract-summary">
      <Table responsive bordered hover className="table">
        <thead>
          <tr>
            <th className="name-th">{heading('team', 'Team')}</th>
            <th>{heading('contracts', 'Contracts')}</th>
            <th>{heading('years', 'Years')}</th>
            <th>{heading('salary', 'Salary')}</th>
            <th>{heading('marketValue', 'Market Value')}</th>
            <th>{heading('surplus', 'Surplus')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.team_key}
              className={selectedTeam === row.team_key ? 'contract-summary-selected' : ''}
              onClick={() => onTeamSelect(
                selectedTeam === row.team_key ? ALL : row.team_key
              )}
            >
              <td className="name-td contract-summary-team">
                <img src={row.team_logo_url} alt="" className="team-logo" />
                <span>{row.team_name}</span>
              </td>
              <td>{row.contracts}</td>
              <td>{row.years}</td>
              <td>{Math.round(row.salary)}</td>
              <td>{Math.round(row.marketValue)}</td>
              <td>{Math.round(row.surplus)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default ContractSummary;
